// src/components/sidebarcart/CartItemList.js
import React, { useContext } from 'react';
import { useAuth } from '../../context/AuthContext';
import PropTypes from 'prop-types';
import { Table, Typography, Space, Button, Spin, Tag, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined, MinusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import ItemQuantityInput from './CartItemQuantityInput';
import CartContext from '../../context/CartContext';

const { Text } = Typography;

function CartItemList({ cartDetails, onRemoveItem, onUpdateQuantity, onUpdateSplitter, isLoadingCart }) {
    const { 
        getDisplayRetailPrice, 
        toggleVolumePrice,
        getVolumePrice,
        isVolumePricingActive,
        getVolumePricingTier
    } = useContext(CartContext);
    const { user } = useAuth();
    const isCreditCardMarkupEnabled = !!user?.creditCardMarkupEnabled;

    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) {
            return 'N/A';
        }
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    if (isLoadingCart) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (cartDetails.length === 0) {
        return (
            <Typography.Text style={{ color: '#8c8c8c', padding: '20px', textAlign: 'center', display: 'block' }}>
                El carrito está vacío.
            </Typography.Text>
        );
    }

    const columns = [
        {
            title: 'MARCA',
            dataIndex: 'BrandName',
            key: 'marca',
            width: 70,
            ellipsis: true,
            render: (text, record) => (
                <Text style={{ fontSize: '14px', color: record.isCustom ? '#ffd700' : '#595959' }}>
                    {record.isCustom ? (
                        <Tag color="gold" style={{ margin: 0, fontSize: '13px' }}>CUSTOM</Tag>
                    ) : (
                        text || 'N/A'
                    )}
                </Text>
            )
        },
        {
            title: 'PRODUCTO',
            dataIndex: 'ProductCode',
            key: 'producto',
            width: 100,
            ellipsis: true,
            render: (text, record) => (
                <Text strong style={{ fontSize: '14px', color: record.isCustom ? '#ffd700' : '#262626' }}>
                    {text || 'N/A'}
                </Text>
            )
        },
        {
            title: 'DESCRIPCION',
            dataIndex: 'ProductDescription',
            key: 'descripcion',
            width: 200,
            render: (text, record) => (
                <Text style={{ 
                    fontSize: '13px', 
                    color: record.isCustom ? '#ffd700' : '#595959',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                }}>
                    {text || '-'}
                </Text>
            )
        },
        {
            title: 'PRECIO',
            key: 'precio',
            width: 160,
            align: 'right',
            render: (_, record) => {
                // Skip volume pricing for custom products
                if (record.isCustom) {
                    const price = record.RetailPrice || 0;
                    const splitter = record.splitter || 1;
                    const unitPrice = price / splitter;
                    return (
                        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                            <Text strong style={{ fontSize: '14px', color: '#ffd700' }}>
                                {formatPrice(unitPrice)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '11px', color: 'rgba(255, 215, 0, 0.6)' }}>
                                (Personalizado)
                            </Text>
                        </Space>
                    );
                }
                
                const basePrice = getDisplayRetailPrice(record.RetailPrice);
                const isVolumeActive = isVolumePricingActive(record.productId);
                const volumePrice = getVolumePrice(basePrice, record.productId);
                const displayPrice = isVolumeActive ? volumePrice : basePrice;
                const volumeTier = getVolumePricingTier(record.productId);
                const splitter = record.splitter || 1;
                const unitPrice = displayPrice / splitter;
                
                return (
                    <Space direction="vertical" size={2} style={{ alignItems: 'flex-end', width: '100%' }}>
                        <Space size={4} style={{ justifyContent: 'flex-end' }}>
                            <Text 
                                strong 
                                style={{ 
                                    fontSize: '14px', 
                                    color: volumeTier ? volumeTier.color : (splitter > 1 ? '#e05a3a' : '#262626'),
                                    fontWeight: isVolumeActive ? 'bold' : 'normal'
                                }}
                            >
                                {formatPrice(unitPrice)}
                                {volumeTier && volumeTier.markupPercent > 0 && (
                                    <Text style={{ fontSize: '11px', color: volumeTier.color, marginLeft: '2px' }}>
                                        (+{volumeTier.markupPercent}%)
                                    </Text>
                                )}
                            </Text>
                            <Tooltip title={isVolumeActive ? "Desactivar precio por volumen" : "Activar precio por volumen"}>
                                {!isCreditCardMarkupEnabled && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={
                                        <ThunderboltOutlined
                                            style={{
                                                color: isVolumeActive ? '#ffd700' : '#d4a017',
                                                fontSize: '14px'
                                            }}
                                        />
                                    }
                                    onClick={() => toggleVolumePrice(record.productId)}
style={{
                                        padding: '0 4px',
                                        height: '20px',
                                        minWidth: '20px'
                                    }}
                                />
                                )}
                            </Tooltip>
                        </Space>
                        {isVolumeActive && (
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                ({formatPrice(basePrice / splitter)})
                            </Text>
                        )}
                    </Space>
                );
            }
        },
        {
            title: 'FRACC.',
            dataIndex: 'splitter',
            key: 'fraccion',
            width: 155,
            align: 'center',
            render: (splitter, record) => (
                <ItemQuantityInput
                    initialQuantity={splitter || 1}
                    productId={record.productId}
                    onUpdateQuantity={(productId, newSplitter) => {
                        if (onUpdateSplitter) {
                            onUpdateSplitter(productId, newSplitter);
                        }
                    }}
                />
            )
        },
        {
            title: 'CANT.',
            dataIndex: 'quantity',
            key: 'cantidad',
            width: 140,
            align: 'center',
            render: (quantity, record) => (
                <ItemQuantityInput
                    initialQuantity={quantity}
                    productId={record.productId}
                    onUpdateQuantity={onUpdateQuantity}
                />
            )
        },
        {
            title: '',
            key: 'actions',
            width: 40,
            align: 'center',
            render: (_, record) => (
                <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => onRemoveItem(record.productId)}
                    style={{ padding: '4px 8px' }}
                />
            )
        }
    ];

    return (
        <div style={{ overflowY: 'auto', height: '100%' }}>
            <Table
                dataSource={cartDetails}
                columns={columns}
                rowKey={(record) => record.productId}
                pagination={false}
                size="small"
                style={{ 
                    background: '#fff',
                    borderRadius: '8px'
                }}
                components={{
                    header: {
                        cell: (props) => (
                            <th {...props} style={{
                                ...props.style,
                                background: '#fafafa',
                                color: '#595959',
                                fontWeight: 600,
                                fontSize: '13px',
                                padding: '12px 8px',
                                borderBottom: '1px solid #f0f0f0',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }} />
                        )
                    }
                }}
            />
        </div>
    );
}

CartItemList.propTypes = {
    cartDetails: PropTypes.array.isRequired,
    onRemoveItem: PropTypes.func.isRequired,
    onUpdateQuantity: PropTypes.func.isRequired,
    onUpdateSplitter: PropTypes.func,
    isLoadingCart: PropTypes.bool.isRequired,
};

// Memoize to prevent unnecessary re-renders
export default React.memo(CartItemList, (prevProps, nextProps) => {
    // Only re-render if cartDetails array changes or loading state changes
    if (prevProps.isLoadingCart !== nextProps.isLoadingCart) {
        return false; // Re-render if loading state changed
    }
    
    if (prevProps.cartDetails.length !== nextProps.cartDetails.length) {
        return false; // Re-render if number of items changed
    }
    
    // Check if any item's relevant properties changed
    for (let i = 0; i < prevProps.cartDetails.length; i++) {
        const prevItem = prevProps.cartDetails[i];
        const nextItem = nextProps.cartDetails[i];
        
        if (
            prevItem.productId !== nextItem.productId ||
            prevItem.quantity !== nextItem.quantity ||
            prevItem.splitter !== nextItem.splitter ||
            prevItem.RetailPrice !== nextItem.RetailPrice
        ) {
            return false; // Re-render if any item changed
        }
    }
    
    return true; // Skip re-render if nothing changed
});